
import { useEffect, useRef, useState } from 'react';

interface RollingGalleryProps {
  images: string[];
  autoplay?: boolean;
  pauseOnHover?: boolean;
}

export default function RollingGallery({ 
  images, 
  autoplay = true, 
  pauseOnHover = true 
}: RollingGalleryProps) {
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!autoplay || !trackRef.current) return;

    const track = trackRef.current;
    let animationId: number;
    let isPaused = false;

    const animate = () => {
      if (!isPaused && track) {
        const currentTransform = getComputedStyle(track).transform;
        const matrix = new DOMMatrix(currentTransform);
        const currentX = matrix.m41;
        
        // Reset position when scrolled past all images
        if (Math.abs(currentX) >= track.scrollWidth / 2) {
          track.style.transform = 'translateX(0px)';
        } else {
          track.style.transform = `translateX(${currentX - 0.5}px)`;
        }
      }
      animationId = requestAnimationFrame(animate);
    };

    const handleMouseEnter = () => {
      if (pauseOnHover) isPaused = true;
    };

    const handleMouseLeave = () => {
      if (pauseOnHover) isPaused = false;
    };

    if (pauseOnHover) {
      track.addEventListener('mouseenter', handleMouseEnter);
      track.addEventListener('mouseleave', handleMouseLeave);
    }

    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
      if (pauseOnHover) {
        track.removeEventListener('mouseenter', handleMouseEnter);
        track.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [autoplay, pauseOnHover]);

  // Duplicate images for seamless loop
  const duplicatedImages = [...images, ...images];

  return (
    <div className="relative w-full overflow-hidden bg-gray-900/50 rounded-xl border border-gray-700">
      {/* Gradient overlays */}
      <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-gray-900 to-transparent z-10 pointer-events-none"></div>
      <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-900 to-transparent z-10 pointer-events-none"></div>
      
      {/* Gallery content */}
      <div className="relative">
        <div 
          ref={trackRef}
          className="flex gap-4 py-6"
          style={{ 
            width: `${duplicatedImages.length * 320}px`,
            transform: 'translateX(0px)',
            transition: autoplay ? 'none' : 'transform 0.3s ease'
          }}
        >
          {duplicatedImages.map((image, index) => (
            <div 
              key={index}
              className="flex-shrink-0 w-80 h-48 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <img 
                src={image}
                alt={`Gartenarbeit ${(index % images.length) + 1}`}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                draggable={false}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
